import { useEffect, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "motion/react";
import {
  ArrowRight,
  Building2,
  Eye,
  FileCheck2,
  Fingerprint,
  Landmark,
  Megaphone,
  Route,
  Scale,
  ShieldCheck,
  Sparkles,
  UserRoundCheck,
  Users,
} from "lucide-react";
import { ButtonLink } from "../../components/ui";
import { useLang } from "../../lib/i18n";
import { api } from "../../lib/api";
import { fmtNumber } from "../../lib/format";
import { useDocumentTitle } from "../../lib/hooks";

const ease = [0.2, 0.7, 0.2, 1] as const;

function Reveal({ children, delay = 0, className }: { children: ReactNode; delay?: number; className?: string }) {
  const reduce = useReducedMotion();
  return (
    <motion.div
      className={className}
      initial={reduce ? false : { opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.8, ease, delay }}
    >
      {children}
    </motion.div>
  );
}

const CHAIN = [
  { k: "Authority", hk: "ताक़त", d: "Power the people delegate — to representatives, departments and public servants.", hd: "वो ताक़त जो जनता सौंपती है — नेताओं को, विभागों को, सरकारी कर्मचारियों को।", icon: Landmark },
  { k: "Responsibility", hk: "ज़िम्मेदारी", d: "Who is answerable for a road, a school, a promise, a decision.", hd: "किसी सड़क, स्कूल, वादे या फ़ैसले का जवाब किसे देना है।", icon: Route },
  { k: "Outcome", hk: "नतीजा", d: "What actually happened to the citizen — preserved with evidence and dates.", hd: "आम आदमी के साथ असल में क्या हुआ — सबूत और तारीख़ के साथ दर्ज।", icon: FileCheck2 },
  { k: "Accountability", hk: "जवाबदेही", d: "The response, the non-response, and the record of both.", hd: "जवाब मिला या नहीं मिला — दोनों का रिकॉर्ड।", icon: Scale },
  { k: "Learning", hk: "सबक़", d: "Patterns that let institutions and citizens do better next time.", hd: "वो बातें जिनसे सरकारी संस्थाएँ और हम सब अगली बार बेहतर कर सकें।", icon: Sparkles },
];

const PRINCIPLES = [
  { icon: Scale, t: "An allegation is not a verdict", ht: "आरोप, फ़ैसला नहीं होता", d: "Every record distinguishes what a citizen reported from what was verified, what an authority found, and what finally happened.", hd: "हर रिकॉर्ड में अलग-अलग साफ़ लिखा होता है — नागरिक ने क्या बताया, किस बात की जाँच हुई, अधिकारियों ने क्या पाया, और आख़िर में क्या हुआ।" },
  { icon: Fingerprint, t: "Your identity stays yours", ht: "आपकी पहचान आपकी ही रहेगी", d: "Complainant identity is held apart from the case. Representatives, parties and volunteers never see it without an explicit, audited grant.", hd: "शिकायत करने वाले की पहचान मामले से अलग रखी जाती है। नेता, पार्टियाँ या वॉलंटियर इसे तब तक नहीं देख सकते जब तक साफ़ इजाज़त न मिले — और वो इजाज़त भी दर्ज होती है।" },
  { icon: ShieldCheck, t: "Evidence is preserved, untouched", ht: "सबूत जैसे के तैसे सुरक्षित", d: "Files are stored privately and fingerprinted (SHA-256) the moment they arrive, so the record can prove it has not changed.", hd: "फ़ाइलें आते ही निजी तौर पर रखी जाती हैं और उनकी डिजिटल पहचान (SHA-256) दर्ज कर ली जाती है, ताकि साबित हो सके कि उनमें कोई छेड़छाड़ नहीं हुई।" },
  { icon: Building2, t: "Routed to lawful authority", ht: "सही विभाग तक पहुँचाया जाता है", d: "You do not need to know which department is responsible. Seva Sangrah classifies the problem and routes it to the institution that is.", hd: "आपको यह जानने की ज़रूरत नहीं कि कौन-सा विभाग ज़िम्मेदार है। सेवा संग्रह समस्या को समझकर उसे सही विभाग तक पहुँचाता है।" },
  { icon: Eye, t: "Every action leaves a trace", ht: "हर क़दम का रिकॉर्ड रहता है", d: "Each status change, response, grant and decision is attributable to a named person, permanently.", hd: "हर बदलाव, हर जवाब, हर इजाज़त और हर फ़ैसले के साथ हमेशा दर्ज रहता है कि वो किसने किया।" },
  { icon: Users, t: "The citizen is not the API", ht: "दफ़्तरों के चक्कर काटना आपका काम नहीं", d: "When the State makes you carry the same papers from office to office, that journey itself becomes evidence of what must change.", hd: "जब सरकार आपसे वही काग़ज़ एक दफ़्तर से दूसरे दफ़्तर ले जाने को कहती है, तो वो भागदौड़ ख़ुद सबूत बन जाती है कि क्या बदलना चाहिए।" },
];

export default function Home() {
  const { t, tx, lang } = useLang();
  const hi = lang === "hi";
  useDocumentTitle("");
  const reduce = useReducedMotion();
  const [stats, setStats] = useState<any>(null);
  useEffect(() => {
    api.get("/api/public/stats").then(setStats).catch(() => setStats(null));
  }, []);

  return (
    <div className="overflow-x-clip">
      {/* ================================================================ HERO */}
      <section className="relative isolate overflow-hidden bg-ink-950 text-white">
        <div aria-hidden className="pointer-events-none absolute inset-0 -z-10">
          <div className="absolute -left-[10%] -top-[20%] h-[70vh] w-[70vw] rounded-full bg-[radial-gradient(closest-side,rgba(52,77,140,0.65),transparent)] blur-3xl animate-drift" />
          <div className="absolute -right-[15%] top-[10%] h-[65vh] w-[55vw] rounded-full bg-[radial-gradient(closest-side,rgba(232,131,58,0.42),transparent)] blur-3xl animate-drift-slow" />
          <div className="absolute bottom-[-25%] left-[25%] h-[60vh] w-[50vw] rounded-full bg-[radial-gradient(closest-side,rgba(53,104,82,0.5),transparent)] blur-3xl animate-drift" />
          <div className="absolute inset-0 grid-fade" />
          <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-b from-transparent to-ink-950" />
        </div>

        <div className="mx-auto grid min-h-[100svh] max-w-7xl items-center gap-16 px-5 pb-24 pt-32 sm:px-8 lg:grid-cols-[1.25fr_0.75fr] lg:pt-28">
          <div>
            <motion.div initial={reduce ? false : { opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease }}>
              <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3.5 py-1.5 text-[12.5px] font-medium text-white/80 backdrop-blur">
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-saffron-400 opacity-60" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-saffron-500" />
                </span>
                {t("hero_kicker")}
              </span>
            </motion.div>
            <h1 className="display mt-7 text-[clamp(3.4rem,10vw,8.6rem)] text-white">
              <motion.span className="block" initial={reduce ? false : { opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, ease, delay: 0.1 }}>
                {t("hero_title_1")}
              </motion.span>
              <motion.span
                className="block italic bg-gradient-to-r from-saffron-400 via-[#ffd2a8] to-white bg-clip-text text-transparent pb-2"
                initial={reduce ? false : { opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, ease, delay: 0.25 }}
              >
                {t("hero_title_2")}
              </motion.span>
            </h1>
            <motion.p
              className="mt-7 max-w-xl text-[17px] leading-relaxed text-white/70 sm:text-[19px]"
              initial={reduce ? false : { opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease, delay: 0.45 }}
            >
              {t("hero_body")}
            </motion.p>
            <motion.div className="mt-10 flex flex-col gap-3 sm:flex-row" initial={reduce ? false : { opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease, delay: 0.6 }}>
              <ButtonLink to="/complaint" variant="saffron" size="lg">
                {t("cta_complaint")} <ArrowRight className="h-4 w-4" />
              </ButtonLink>
              <ButtonLink to="/promise/new" variant="light" size="lg">
                {t("cta_promise")}
              </ButtonLink>
            </motion.div>
            <motion.p className="mt-6 text-[13px] text-white/45" initial={reduce ? false : { opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1 }}>
              {tx("No account needed to report. Your identity is protected.", "शिकायत करने के लिए खाता बनाना ज़रूरी नहीं। आपकी पहचान सुरक्षित रहती है।")}
            </motion.p>
          </div>

          <MemoryLine />
        </div>
      </section>

      {/* ================================================================ FREEDOM */}
      <section className="relative bg-paper">
        <div className="mx-auto max-w-5xl px-5 py-28 text-center sm:px-8 sm:py-36">
          <Reveal>
            <div className="eyebrow text-saffron-600">{tx("Why this exists", "यह क्यों ज़रूरी है")}</div>
            <h2 className="display mx-auto mt-6 max-w-4xl text-[clamp(2.4rem,5.6vw,4.6rem)] text-ink-900 text-balance">{t("freedom_title")}</h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mx-auto mt-8 max-w-2xl text-[17px] leading-[1.75] text-ink-500">
              {tx(
                "Too many citizens still carry their own files from office to office, wait months without an answer, and watch promises fade from public memory. Democracy does not lack elections. What it lacks is a continuous, faithful record of the relationship between citizens and the institutions that act in their name.",
                "आज भी बहुत से लोग अपनी फ़ाइल लेकर एक दफ़्तर से दूसरे दफ़्तर भटकते हैं, महीनों जवाब का इंतज़ार करते हैं, और देखते हैं कि चुनावी वादे धीरे-धीरे सबके ज़ेहन से मिट जाते हैं। हमारे लोकतंत्र में चुनावों की कमी नहीं है। कमी है एक ऐसे सच्चे और लगातार रिकॉर्ड की, जो बताए कि जनता और उसके नाम पर काम करने वाली संस्थाओं के बीच असल में क्या हो रहा है।",
              )}
            </p>
            <p className="mx-auto mt-5 max-w-2xl text-[17px] leading-[1.75] text-ink-900 font-medium">
              {tx(
                "Seva Sangrah is that record — built by citizens, open in its method, and owned by no government, party or company.",
                "सेवा संग्रह वही रिकॉर्ड है — आम नागरिकों का बनाया हुआ, पूरी तरह पारदर्शी, और किसी सरकार, पार्टी या कंपनी का नहीं।",
              )}
            </p>
          </Reveal>
        </div>
      </section>

      {/* ================================================================ CHAIN */}
      <section className="bg-paper pb-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <div className="mx-auto max-w-2xl text-center">
              <div className="eyebrow text-sage-600">{tx("The accountability chain", "जवाबदेही की कड़ी")}</div>
              <h2 className="mt-4 text-[clamp(1.9rem,3.6vw,2.8rem)] font-semibold tracking-tight text-ink-900 text-balance">{tx("Five links. Each one remembered.", "पाँच कड़ियाँ। हर एक याद रखी जाती है।")}</h2>
            </div>
          </Reveal>
          <div className="relative mt-16 grid gap-5 md:grid-cols-5">
            <div aria-hidden className="absolute left-[10%] right-[10%] top-9 hidden h-px bg-gradient-to-r from-transparent via-ink-200 to-transparent md:block" />
            {CHAIN.map((c, i) => (
              <Reveal key={c.k} delay={i * 0.08}>
                <div className="relative card h-full p-6 text-center md:text-left">
                  <div className="mx-auto grid h-12 w-12 place-items-center rounded-2xl bg-ink-900 text-white shadow-[var(--shadow-lift)] md:mx-0">
                    <c.icon className="h-5 w-5" />
                  </div>
                  <div className="mt-5 text-[12px] font-semibold tabular-nums text-saffron-600">0{i + 1}</div>
                  <h3 className="mt-1 text-[17px] font-semibold text-ink-900">{hi ? c.hk : c.k}</h3>
                  <p className="mt-2 text-[14px] leading-relaxed text-ink-500">{hi ? c.hd : c.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ================================================================ TWO BRANCHES */}
      <section className="bg-white py-28 border-y hairline">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <div className="max-w-2xl">
              <div className="eyebrow text-saffron-600">{tx("Report", "शिकायत")}</div>
              <h2 className="mt-4 text-[clamp(1.9rem,3.6vw,2.8rem)] font-semibold tracking-tight text-ink-900 text-balance">{tx("Tell the Republic what happened. We will make sure it is remembered.", "बताइए आपके साथ क्या हुआ। हम पक्का करेंगे कि यह भुलाया न जाए।")}</h2>
              <p className="mt-4 text-[16px] leading-relaxed text-ink-500">{tx("Two clear paths. You never need to know which department is responsible — that is our job.", "दो सीधे रास्ते। कौन-सा विभाग ज़िम्मेदार है, यह जानना आपका काम नहीं — यह हमारा काम है।")}</p>
            </div>
          </Reveal>
          <div className="mt-14 grid gap-6 lg:grid-cols-2">
            <Reveal>
              <BranchCard
                tone="ink"
                icon={Building2}
                title={tx("Infrastructure & public services", "सड़क, पानी, बिजली और सरकारी सेवाएँ")}
                body={tx(
                  "Roads, water, electricity, drainage, sanitation, schools, hospitals, transport, government schemes, certificates and records.",
                  "सड़क, पानी, बिजली, नाली, सफ़ाई, स्कूल, अस्पताल, बस-परिवहन, सरकारी योजनाएँ, प्रमाण पत्र और रिकॉर्ड।",
                )}
                items={hi ? ["सड़क", "पानी", "बिजली", "नाली", "कूड़ा", "स्कूल", "अस्पताल", "परिवहन", "योजनाएँ"] : ["Roads", "Water supply", "Electricity", "Drainage", "Garbage", "Schools", "Hospitals", "Transport", "Schemes"]}
                to="/complaint?branch=infrastructure"
                cta={tx("Report a public-service problem", "सरकारी सेवा की समस्या बताइए")}
              />
            </Reveal>
            <Reveal delay={0.1}>
              <BranchCard
                tone="saffron"
                icon={UserRoundCheck}
                title={tx("Public servant conduct", "सरकारी कर्मचारी का बर्ताव")}
                body={tx(
                  "Corruption, bribery, harassment, abuse of authority, negligence, delay or refusal of duty — recorded as an allegation, protected as one, and never presented as proof.",
                  "भ्रष्टाचार, रिश्वत, परेशान करना, पद का ग़लत इस्तेमाल, लापरवाही, देरी या काम से मना करना — इसे आरोप के रूप में दर्ज और सुरक्षित किया जाता है, कभी सबूत की तरह पेश नहीं किया जाता।",
                )}
                items={hi ? ["भ्रष्टाचार", "रिश्वत", "परेशान करना", "पद का ग़लत इस्तेमाल", "लापरवाही", "देरी", "काम से मना करना"] : ["Corruption", "Bribery", "Harassment", "Abuse of authority", "Negligence", "Delay", "Refusal of duty"]}
                to="/complaint?branch=public_servant"
                cta={tx("Report conduct of a public servant", "किसी कर्मचारी की शिकायत करें")}
              />
            </Reveal>
          </div>
        </div>
      </section>

      {/* ================================================================ PROMISES */}
      <section className="relative overflow-hidden bg-ink-950 py-28 text-white">
        <div aria-hidden className="pointer-events-none absolute -right-40 top-0 h-[520px] w-[520px] rounded-full bg-[radial-gradient(closest-side,rgba(53,104,82,0.55),transparent)] blur-3xl" />
        <div className="relative mx-auto grid max-w-7xl gap-14 px-5 sm:px-8 lg:grid-cols-2 lg:items-center">
          <Reveal>
            <div className="eyebrow text-[#9fd0b4]">{tx("Promises", "वादे")}</div>
            <h2 className="display mt-5 text-[clamp(2.6rem,5.5vw,4.4rem)]">{tx("A promise made in public belongs to the public.", "जो वादा जनता से किया, उस पर जनता का हक़ है।")}</h2>
            <p className="mt-6 max-w-lg text-[17px] leading-relaxed text-white/65">
              {tx(
                "Record what was promised, by whom, where and by when — with the original source. Each promise is verified before it is published, then tracked with evidence until it is fulfilled, or not.",
                "दर्ज कीजिए कि क्या वादा किया गया, किसने किया, कहाँ और कब तक — असली स्रोत के साथ। हर वादे की पहले जाँच होती है, फिर उसे छापा जाता है, और सबूतों के साथ नज़र रखी जाती है कि वो पूरा हुआ या नहीं।",
              )}
            </p>
            <div className="mt-9 flex flex-col gap-3 sm:flex-row">
              <ButtonLink to="/promise/new" variant="saffron" size="lg">
                {tx("Record a promise", "वादा दर्ज करें")} <ArrowRight className="h-4 w-4" />
              </ButtonLink>
              <ButtonLink to="/promises" variant="light" size="lg">
                {tx("Browse the registry", "सारे वादे देखें")}
              </ButtonLink>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <ol className="space-y-3">
              {(hi
                ? [
                    ["दर्ज", "कोई नागरिक वादे के असली शब्द, किसने किया, किस इलाक़े के लिए, और कहाँ किया — यह सब दर्ज करता है।"],
                    ["जाँच", "छपने से पहले हमारी टीम स्रोत की जाँच करती है।"],
                    ["निगरानी", "काम कितना आगे बढ़ा, इसका रिकॉर्ड तारीख़ वाले सबूतों के साथ रखा जाता है।"],
                    ["जनता की राय", "लोग अपनी राय देते हैं; उस इलाक़े के वोटरों की राय अलग से गिनी जाती है।"],
                  ]
                : [
                    ["Submitted", "A citizen records the exact words, the maker, the scope and the source."],
                    ["Verified", "Reviewers check the source before anything is published."],
                    ["Tracked", "Progress and fulfilment are recorded with dated evidence."],
                    ["Answered", "Citizens respond; constituents' votes are counted separately."],
                  ]
              ).map(([k, d], i) => (
                <li key={k} className="flex gap-5 rounded-2xl border border-white/10 bg-white/[0.04] p-5 backdrop-blur">
                  <div className="text-[13px] font-semibold tabular-nums text-saffron-400">0{i + 1}</div>
                  <div>
                    <div className="font-semibold">{k}</div>
                    <div className="mt-1 text-[14px] leading-relaxed text-white/60">{d}</div>
                  </div>
                </li>
              ))}
            </ol>
          </Reveal>
        </div>
      </section>

      {/* ================================================================ PRINCIPLES */}
      <section className="bg-paper py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <div className="max-w-2xl">
              <div className="eyebrow text-sage-600">{tx("Principles", "हमारे उसूल")}</div>
              <h2 className="mt-4 text-[clamp(1.9rem,3.6vw,2.8rem)] font-semibold tracking-tight text-ink-900 text-balance">{tx("Built to be trusted by the citizen who reports — and by the institution that answers.", "ऐसा बनाया गया है कि शिकायत करने वाला नागरिक भी भरोसा कर सके — और जवाब देने वाली संस्था भी।")}</h2>
            </div>
          </Reveal>
          <div className="mt-14 grid gap-px overflow-hidden rounded-[28px] border hairline bg-ink-100 sm:grid-cols-2 lg:grid-cols-3">
            {PRINCIPLES.map((p, i) => (
              <Reveal key={p.t} delay={(i % 3) * 0.06} className="bg-white p-8">
                <p.icon className="h-6 w-6 text-saffron-600" />
                <h3 className="mt-5 text-[17px] font-semibold text-ink-900">{hi ? p.ht : p.t}</h3>
                <p className="mt-2 text-[14.5px] leading-relaxed text-ink-500">{hi ? p.hd : p.d}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ================================================================ LIVE RECORD */}
      <section className="bg-white py-24 border-y hairline">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
              <div>
                <div className="eyebrow text-saffron-600">{tx("The record so far", "अब तक का रिकॉर्ड")}</div>
                <h2 className="mt-4 text-[clamp(1.9rem,3.6vw,2.6rem)] font-semibold tracking-tight text-ink-900">
                  {stats?.observed?.complaintsRecorded ? tx("Every number below is a real submission.", "नीचे का हर आँकड़ा असली शिकायतों से है।") : tx("The record begins with you.", "यह रिकॉर्ड आपसे शुरू होता है।")}
                </h2>
              </div>
              <Link to="/record" className="inline-flex items-center gap-1.5 text-[14px] font-medium text-ink-800 hover:gap-2.5 transition-all">
                {tx("Explore the civic record", "जनता का रिकॉर्ड देखें")} <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
          <div className="mt-12 grid grid-cols-2 gap-px overflow-hidden rounded-[24px] border hairline bg-ink-100 lg:grid-cols-4">
            {[
              [tx("Complaints recorded", "दर्ज शिकायतें"), stats?.observed?.complaintsRecorded],
              [tx("Marked resolved", "सुलझी हुई"), stats?.observed?.resolved],
              [tx("States represented", "कितने राज्यों से"), stats?.observed?.statesRepresented],
              [tx("Published promises", "छपे हुए वादे"), stats?.observed?.publishedPromises],
            ].map(([label, v]) => (
              <div key={label as string} className="bg-white p-7">
                <div className="text-[clamp(2rem,4.5vw,3.2rem)] font-semibold tracking-tight tabular-nums text-ink-900">{v === undefined ? "—" : fmtNumber(v as number)}</div>
                <div className="mt-1 text-[13px] text-ink-500">{label}</div>
              </div>
            ))}
          </div>
          <p className="mt-4 text-[12.5px] text-ink-400">
            {tx(
              "Observed counts of submissions as recorded. A complaint is a citizen's report or allegation — not a finding of wrongdoing.",
              "ये आँकड़े दर्ज हुई शिकायतों की गिनती हैं। शिकायत किसी नागरिक की बात या आरोप है — इससे किसी की ग़लती साबित नहीं होती।",
            )}
            {stats?.environment && stats.environment !== "production" ? tx(" This is a demonstration environment: figures are synthetic.", " यह डेमो वाली साइट है: आँकड़े नक़ली हैं।") : ""}
          </p>
        </div>
      </section>

      {/* ================================================================ STANDARD QUOTE */}
      <section className="bg-paper py-28">
        <Reveal className="mx-auto max-w-5xl px-5 text-center sm:px-8">
          <Megaphone className="mx-auto h-7 w-7 text-saffron-500" />
          <blockquote className="display mt-8 text-[clamp(2.2rem,5vw,4.2rem)] text-ink-900 text-balance">
            {hi ? (
              <>
                “हम लोकतंत्र को एक साँचे में नहीं ढालते। हम लोकतंत्र की <span className="italic text-sage-600">याददाश्त</span> मज़बूत करते हैं।”
              </>
            ) : (
              <>
                “We don’t standardise democracy. We standardise the ability of democracy <span className="italic text-sage-600">to remember.</span>”
              </>
            )}
          </blockquote>
          <p className="mx-auto mt-8 max-w-2xl text-[15px] leading-relaxed text-ink-500">
            {tx(
              "Seva Sangrah stays below the level of political sovereignty. It does not govern, legislate, adjudicate or replace courts. It provides the memory — what was promised, what responsibility existed, what citizens experienced, what was done, and what was learned.",
              "सेवा संग्रह राजनीति से ऊपर नहीं, उसके नीचे रहकर काम करता है। यह न सरकार चलाता है, न क़ानून बनाता है, न फ़ैसले सुनाता है, न अदालत की जगह लेता है। यह बस याद रखता है — क्या वादा हुआ, ज़िम्मेदारी किसकी थी, लोगों ने क्या झेला, क्या किया गया, और उससे क्या सीखा।",
            )}
          </p>
        </Reveal>
      </section>

      {/* ================================================================ JOIN */}
      <section className="relative overflow-hidden bg-ink-950 py-28 text-white">
        <div aria-hidden className="pointer-events-none absolute left-1/2 top-1/2 h-[600px] w-[900px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[radial-gradient(closest-side,rgba(232,131,58,0.28),transparent)] blur-3xl" />
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal className="mx-auto max-w-3xl text-center">
            <div className="eyebrow text-saffron-400">{tx("Join Seva Sangrah", "सेवा संग्रह से जुड़िए")}</div>
            <h2 className="display mt-5 text-[clamp(2.6rem,6vw,5rem)] text-balance">{t("join_title")}</h2>
            <p className="mx-auto mt-6 max-w-xl text-[17px] leading-relaxed text-white/65">
              {tx(
                "Every lasting institution was built by a few people who decided to begin. Contribute your time, your expertise or your organisation.",
                "हर बड़ी संस्था की शुरुआत कुछ ऐसे लोगों ने की जिन्होंने ठान लिया। अपना समय दीजिए, अपना हुनर दीजिए, या अपनी संस्था के साथ जुड़िए।",
              )}
            </p>
          </Reveal>
          <div className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              ["/join/founding-citizen", tx("Founding Citizen", "संस्थापक नागरिक"), tx("Help build awareness, verify records and preserve institutional memory.", "लोगों को जागरूक कीजिए, रिकॉर्ड की जाँच में मदद कीजिए, और देश की याददाश्त सँभालिए।")],
              ["/join/expert", tx("Expert Contributor", "विशेषज्ञ"), tx("Lend professional expertise in law, policy, research, technology or design.", "क़ानून, नीति, रिसर्च, टेक्नोलॉजी या डिज़ाइन में अपना हुनर दीजिए।")],
              ["/join/civil-society", tx("Civil Society / NGO", "सामाजिक संगठन / NGO"), tx("Partner as an organisation working with communities.", "लोगों के बीच काम करने वाली संस्था के रूप में साथ आइए।")],
              ["/connect", tx("Connect with us", "हमसे संपर्क करें"), tx("Explore partnership, research or institutional collaboration.", "साझेदारी, रिसर्च या संस्था के स्तर पर साथ काम करने के बारे में बात कीजिए।")],
            ].map(([to, title, body], i) => (
              <Reveal key={to} delay={i * 0.06}>
                <Link to={to} className="group flex h-full flex-col justify-between rounded-3xl border border-white/10 bg-white/[0.04] p-6 transition-all hover:-translate-y-1 hover:border-white/25 hover:bg-white/[0.07]">
                  <div>
                    <h3 className="text-[17px] font-semibold">{title}</h3>
                    <p className="mt-2 text-[14px] leading-relaxed text-white/60">{body}</p>
                  </div>
                  <ArrowRight className="mt-8 h-5 w-5 text-saffron-400 transition-transform group-hover:translate-x-1" />
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

function BranchCard({ tone, icon: Icon, title, body, items, to, cta }: { tone: "ink" | "saffron"; icon: any; title: string; body: string; items: string[]; to: string; cta: string }) {
  const dark = tone === "ink";
  return (
    <Link
      to={to}
      className={`group relative flex h-full flex-col overflow-hidden rounded-[28px] p-8 sm:p-10 transition-all duration-300 hover:-translate-y-1 ${dark ? "bg-ink-900 text-white shadow-[var(--shadow-lift)]" : "bg-saffron-50 text-ink-900 ring-1 ring-saffron-200"}`}
    >
      <div className={`grid h-12 w-12 place-items-center rounded-2xl ${dark ? "bg-white/10" : "bg-white text-saffron-600 shadow-sm"}`}>
        <Icon className="h-6 w-6" />
      </div>
      <h3 className="mt-8 text-[24px] font-semibold tracking-tight">{title}</h3>
      <p className={`mt-3 max-w-md text-[15px] leading-relaxed ${dark ? "text-white/65" : "text-ink-600"}`}>{body}</p>
      <div className="mt-6 flex flex-wrap gap-2">
        {items.map((i) => (
          <span key={i} className={`rounded-full px-3 py-1 text-[12.5px] ${dark ? "bg-white/[0.07] text-white/75" : "bg-white text-ink-600 ring-1 ring-saffron-200/70"}`}>
            {i}
          </span>
        ))}
      </div>
      <div className={`mt-10 inline-flex items-center gap-2 text-[14.5px] font-semibold ${dark ? "text-saffron-400" : "text-saffron-700"}`}>
        {cta} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
      </div>
    </Link>
  );
}

/** An abstract, animated rendering of how a single civic memory forms. No data — purely illustrative. */
function MemoryLine() {
  const reduce = useReducedMotion();
  const { tx } = useLang();
  const steps = [
    { t: tx("A promise is made", "कोई वादा किया जाता है"), s: tx("Recorded with its source", "स्रोत के साथ दर्ज होता है") },
    { t: tx("A citizen reports", "कोई नागरिक शिकायत करता है"), s: tx("Evidence fingerprinted on arrival", "सबूत आते ही सुरक्षित कर लिए जाते हैं") },
    { t: tx("Responsibility is routed", "ज़िम्मेदार तक पहुँचाया जाता है"), s: tx("To the institution with authority", "उस विभाग तक जिसके हाथ में कार्रवाई है") },
    { t: tx("A response — or silence", "जवाब आता है — या चुप्पी"), s: tx("Both become part of the record", "दोनों रिकॉर्ड का हिस्सा बनते हैं") },
    { t: tx("The outcome is remembered", "नतीजा याद रखा जाता है"), s: tx("So the next citizen starts ahead", "ताकि अगले नागरिक को शुरुआत से न जूझना पड़े") },
  ];
  return (
    <div className="relative hidden lg:block" aria-hidden>
      <div className="relative rounded-[32px] border border-white/10 bg-white/[0.04] p-8 backdrop-blur-xl shadow-[0_40px_120px_-40px_rgba(0,0,0,0.8)]">
        <div className="mb-6 flex items-center justify-between">
          <span className="eyebrow text-white/50">{tx("How memory forms", "याददाश्त कैसे बनती है")}</span>
          <span className="h-2 w-2 rounded-full bg-[#9fd0b4]" />
        </div>
        <div className="relative">
          <motion.div
            className="absolute left-[11px] top-2 w-px bg-gradient-to-b from-saffron-400 via-white/40 to-[#9fd0b4]"
            initial={reduce ? false : { height: 0 }}
            animate={{ height: "calc(100% - 16px)" }}
            transition={{ duration: 2.2, ease, delay: 0.6 }}
          />
          <ul className="space-y-6">
            {steps.map((s, i) => (
              <motion.li
                key={s.t}
                className="relative flex gap-5 pl-0"
                initial={reduce ? false : { opacity: 0, x: 12 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.7, ease, delay: 0.7 + i * 0.35 }}
              >
                <span className="relative z-10 mt-1 grid h-6 w-6 shrink-0 place-items-center rounded-full border border-white/25 bg-ink-950">
                  <span className={`h-2 w-2 rounded-full ${i === steps.length - 1 ? "bg-[#9fd0b4]" : "bg-saffron-400"}`} />
                </span>
                <div>
                  <div className="text-[15px] font-medium text-white">{s.t}</div>
                  <div className="mt-0.5 text-[13px] text-white/50">{s.s}</div>
                </div>
              </motion.li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
